/**
 * Plano de ação — vocabulários fechados, tipos e o que a tela deriva deles (issue #359).
 *
 * Os quatro vocabulários espelham `backend/src/types/actionPlan.ts`: mudar um valor aqui sem
 * mudar lá troca o rótulo por um `undefined` silencioso na tabela.
 *
 * O prazo (`dueDate`) **nunca** sai daqui para o servidor: ele é calculado da data de
 * elaboração do plano mais a prioridade, e chega pronto na leitura (D3).
 */

// ============================================================================
// PRIORIDADE E VIGÊNCIA
// ============================================================================

export const PRIORIDADES = ['IRRELEVANTE', 'DE_ATENCAO', 'CRITICA', 'INTOLERAVEL'] as const;
export type ActionPriority = (typeof PRIORIDADES)[number];

/** Numerados como o cliente escreve na planilha, e como o quadro do PGR imprime. */
export const ROTULO_DA_PRIORIDADE: Record<ActionPriority, string> = {
  IRRELEVANTE: '1 - Irrelevante',
  DE_ATENCAO: '2 - De atenção',
  CRITICA: '3 - Crítica',
  INTOLERAVEL: '4 - Intolerável',
};

/**
 * A vigência da planilha do cliente ("6 meses", "2 anos") é função da prioridade em todas as
 * linhas — por isso é rótulo derivado, e não campo (D5).
 */
export const VIGENCIA_DA_PRIORIDADE: Record<ActionPriority, string> = {
  IRRELEVANTE: '2 anos',
  DE_ATENCAO: '6 meses',
  CRITICA: '3 meses',
  INTOLERAVEL: 'Imediato',
};

export const COR_DA_PRIORIDADE: Record<ActionPriority, 'default' | 'info' | 'warning' | 'error'> = {
  IRRELEVANTE: 'default',
  DE_ATENCAO: 'info',
  CRITICA: 'warning',
  INTOLERAVEL: 'error',
};

// ============================================================================
// RESPONSÁVEL, TIPO DE AÇÃO, SITUAÇÃO
// ============================================================================

/** Papel, não usuário do sistema: o empregador tipicamente não tem login (D6). */
export const RESPONSAVEIS = [
  'EMPREGADOR',
  'PROFISSIONAL_SEGURANCA_HABILITADO',
  'PROFISSIONAL_SST',
  'MEDICO_DO_TRABALHO',
  'PROFISSIONAL_HABILITADO',
] as const;
export type ActionResponsible = (typeof RESPONSAVEIS)[number];

export const ROTULO_DO_RESPONSAVEL: Record<ActionResponsible, string> = {
  EMPREGADOR: 'Empregador',
  PROFISSIONAL_SEGURANCA_HABILITADO: 'Profissional de Segurança Habilitado',
  PROFISSIONAL_SST: 'Profissional de SST',
  MEDICO_DO_TRABALHO: 'Médico do Trabalho',
  PROFISSIONAL_HABILITADO: 'Profissional Habilitado',
};

export const TIPOS_DE_ACAO = ['APRIMORAR', 'INTRODUZIR', 'MANTER'] as const;
export type ActionType = (typeof TIPOS_DE_ACAO)[number];

export const ROTULO_DO_TIPO_DE_ACAO: Record<ActionType, string> = {
  APRIMORAR: 'Aprimorar',
  INTRODUZIR: 'Introduzir',
  MANTER: 'Manter',
};

export const SITUACOES_DA_ACAO = ['PLANEJADA', 'EM_ANDAMENTO', 'CONCLUIDA', 'CANCELADA'] as const;
export type ActionItemStatus = (typeof SITUACOES_DA_ACAO)[number];

export const ROTULO_DA_SITUACAO: Record<ActionItemStatus, string> = {
  PLANEJADA: 'Planejada',
  EM_ANDAMENTO: 'Em andamento',
  CONCLUIDA: 'Concluída',
  CANCELADA: 'Cancelada',
};

export const COR_DA_SITUACAO: Record<
  ActionItemStatus,
  'default' | 'info' | 'success' | 'warning'
> = {
  PLANEJADA: 'default',
  EM_ANDAMENTO: 'info',
  CONCLUIDA: 'success',
  CANCELADA: 'warning',
};

// ============================================================================
// EVIDÊNCIA E ORIGEM
// ============================================================================

/** Os nove da legenda "Copiar as Evidências" da planilha do cliente (D9). */
export const TIPOS_DE_EVIDENCIA = [
  'SEM_EVIDENCIAS',
  'TREINAMENTOS',
  'FICHA_DE_ENTREGA',
  'ADEQUACOES_DE_MAQUINAS',
  'CHECKLIST_DE_INSPECOES',
  'DOCUMENTACAO_ASSINADA',
  'PLANO_DE_MANUTENCAO',
  'POP',
  'REGISTRO_FOTOGRAFICO',
] as const;
export type EvidenceType = (typeof TIPOS_DE_EVIDENCIA)[number];

export const ROTULO_DA_EVIDENCIA: Record<EvidenceType, string> = {
  SEM_EVIDENCIAS: 'Sem evidências',
  TREINAMENTOS: 'Treinamentos',
  FICHA_DE_ENTREGA: 'Ficha de entrega',
  ADEQUACOES_DE_MAQUINAS: 'Adequações de máquinas',
  CHECKLIST_DE_INSPECOES: 'Checklist de inspeções',
  DOCUMENTACAO_ASSINADA: 'Documentação assinada',
  PLANO_DE_MANUTENCAO: 'Plano de manutenção',
  POP: 'POP',
  REGISTRO_FOTOGRAFICO: 'Registro fotográfico',
};

export const ORIGENS = ['NORMA', 'RISCO', 'SETOR', 'DOCUMENTO', 'CIPA', 'MANUAL', 'DENUNCIA'] as const;
export type OriginType = (typeof ORIGENS)[number];

export const ROTULO_DA_ORIGEM: Record<OriginType, string> = {
  NORMA: 'Norma',
  RISCO: 'Risco ocupacional',
  SETOR: 'Setor',
  DOCUMENTO: 'Documento',
  CIPA: 'CIPA',
  MANUAL: 'Cadastrada à mão',
  DENUNCIA: 'Denúncia',
};

// ============================================================================
// SITUAÇÃO DO PLANO
// ============================================================================

/**
 * O plano corrente e os ciclos guardados. A empresa pode ter mais de um plano justamente para
 * manter o do ano anterior (D1) — e o quadro do PGR só olha o de elaboração mais recente.
 */
export const SITUACOES_DO_PLANO = ['VIGENTE', 'ENCERRADO'] as const;
export type ActionPlanStatus = (typeof SITUACOES_DO_PLANO)[number];

export const ROTULO_DA_SITUACAO_DO_PLANO: Record<ActionPlanStatus, string> = {
  VIGENTE: 'Vigente',
  ENCERRADO: 'Encerrado',
};

// ============================================================================
// REGISTROS
// ============================================================================

/**
 * A linha da listagem. Ids são `BIGINT.UNSIGNED` no banco: o serviço os normaliza para texto,
 * porque comparar id com `===` contra número devolve falso silencioso.
 */
export interface ActionPlanSummary {
  id: string;
  title: string;
  /** `YYYY-MM-DD`. É dela que sai o prazo de cada ação. */
  elaboratedAt: string;
  status: ActionPlanStatus;
  notes: string | null;
  /** Quantas ações o plano tem — é o que a confirmação de exclusão mostra. */
  totalDeAcoes: number;
  /** Quantas delas estão vencidas, sempre no escopo da empresa em contexto. */
  acoesVencidas: number;
}

/** O plano aberto. Vem sem as ações: elas têm rota própria, com os filtros. */
export interface ActionPlan {
  id: string;
  companyId: string;
  title: string;
  elaboratedAt: string;
  status: ActionPlanStatus;
  notes: string | null;
}

export interface ActionPlanItem {
  id: string;
  actionPlanId: string;
  description: string;
  actionType: ActionType;
  /** O rótulo da NR, como o cliente escreve: "NR 5 - CIPA". */
  norma: string | null;
  priority: ActionPriority;
  /** Calculado no servidor; nulo quando a prioridade é intolerável. */
  dueDate: string | null;
  responsible: ActionResponsible;
  responsibleName: string | null;
  status: ActionItemStatus;
  completedAt: string | null;
  evidenceType: EvidenceType | null;
  evidenceNotes: string | null;
  originType: OriginType;
  originId: string | null;
  originalFileName: string | null;
  fileSize: number | null;
  fileMimeType: string | null;
}

export interface ActionPlanCatalogNorma {
  norma: string;
  /** O enunciado que encabeça as ações daquela norma na planilha do cliente. */
  contexto: string | null;
  quantidade: number;
}

// ============================================================================
// O QUE VAI PARA O SERVIDOR
// ============================================================================

export interface DadosDoPlano {
  title: string;
  elaboratedAt: string;
  status: ActionPlanStatus;
  notes: string | null;
}

/** Sem `dueDate`: o prazo é do servidor, e mandá-lo daqui seria digitá-lo por outro caminho. */
export interface DadosDaAcao {
  description: string;
  actionType: ActionType;
  norma: string | null;
  priority: ActionPriority;
  responsible: ActionResponsible;
  responsibleName: string | null;
  status: ActionItemStatus;
  evidenceType: EvidenceType | null;
  evidenceNotes: string | null;
  originType: OriginType;
  originId: string | null;
}

export type FiltroDePrazo = 'vencidas' | 'a_vencer';

/** Os cinco recortes que o servidor aceita — não há busca por texto no contrato. */
export interface FiltrosDeItens {
  status: ActionItemStatus | '';
  priority: ActionPriority | '';
  norma: string;
  responsible: ActionResponsible | '';
  prazo: FiltroDePrazo | '';
}

export const FILTROS_DE_ITENS_VAZIOS: FiltrosDeItens = {
  status: '',
  priority: '',
  norma: '',
  responsible: '',
  prazo: '',
};

// ============================================================================
// O PRAZO NA TELA
// ============================================================================

export type SituacaoDoPrazo = 'IMEDIATO' | 'VENCIDA' | 'A_VENCER' | 'NO_PRAZO' | 'ENCERRADA';

/** A janela de aviso do chip: dentro dela o prazo já pede atenção (D10). */
export const DIAS_DE_AVISO = 30;

const UM_DIA_EM_MS = 24 * 60 * 60 * 1000;

/**
 * Dias entre hoje e o prazo, contados em UTC sobre a string `YYYY-MM-DD`.
 *
 * A data vem do banco como texto de dia, sem hora. Passá-la por `new Date('2026-09-04')` num
 * fuso a oeste devolve o dia anterior — é a classe de bug que já mordeu o projeto.
 */
export function diasAteOPrazo(dueDate: string, hoje: Date = new Date()): number {
  const [ano, mes, dia] = dueDate.split('-').map(Number);
  const prazo = Date.UTC(ano, mes - 1, dia);
  const referencia = Date.UTC(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  return Math.round((prazo - referencia) / UM_DIA_EM_MS);
}

/**
 * Ação encerrada não é vencida: concluída ou cancelada, o prazo virou histórico, e pintá-lo de
 * vermelho mandaria a pessoa refazer o que já está feito.
 */
export function situacaoDoPrazo(
  item: { dueDate: string | null; status: ActionItemStatus },
  hoje: Date = new Date()
): SituacaoDoPrazo {
  if (item.status === 'CONCLUIDA' || item.status === 'CANCELADA') return 'ENCERRADA';
  if (item.dueDate === null) return 'IMEDIATO';

  const dias = diasAteOPrazo(item.dueDate, hoje);
  if (dias < 0) return 'VENCIDA';
  if (dias <= DIAS_DE_AVISO) return 'A_VENCER';
  return 'NO_PRAZO';
}

/** O que a tela mostra no lugar de uma data que não existe. */
export const SEM_DIA = '—';

/**
 * `YYYY-MM-DD` em `dd/mm/aaaa`, sem passar por `Date` — pelo mesmo motivo de `diasAteOPrazo`.
 *
 * Aceita a ausência porque prazo nulo é caso normal, e não erro: prioridade intolerável não
 * ganha data, e essa ação também é concluída e cancelada como qualquer outra.
 */
export function formatarDia(dia: string | null | undefined): string {
  if (!dia) return SEM_DIA;
  const [ano, mes, dias] = dia.split('-');
  return `${dias}/${mes}/${ano}`;
}
